import type { HTMLAttributes } from 'react';
import { forwardRef, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { Box } from '../../../box';
import { Typography } from '../../../typography';

const LIST_ITEM_HEIGHT = 24; // (px)
const LIST_ITEM_PADDING = 12; // 6 * 2 (px)
const LISTBOX_PADDING = 16; // 8 * 2 (px)
const ESTIMATED_ITEM_HEIGHT = LIST_ITEM_HEIGHT * 2 + LIST_ITEM_PADDING; // estimate the largest possible size

const getListHeight = (item: number) =>
  item * (LIST_ITEM_HEIGHT + LIST_ITEM_PADDING) + LISTBOX_PADDING;

type ReactVirtualListItemOption = [
  React.HTMLAttributes<HTMLLIElement> & { key: any },
  { label: string; value: string },
];

type ReactVirtualListItemProps = {
  listItemProps: ReactVirtualListItemOption[0];
  option: ReactVirtualListItemOption[1];
  dataIndex: number;
};

const ReactVirtualListItem = forwardRef<
  HTMLLIElement,
  ReactVirtualListItemProps
>(({ listItemProps, option, dataIndex }: ReactVirtualListItemProps, ref) => {
  const { key, ...otherListItemProps } = listItemProps;

  return (
    <Typography
      ref={ref}
      component="li"
      data-index={dataIndex}
      {...otherListItemProps}
    >
      {option.label}
    </Typography>
  );
});

// Adapter for @tanstack/react-virtual
export const ReactVirtualListbox = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const parentRef = useRef<HTMLDivElement>(null);
  const { children, ...listProps } = props;

  // Re-type to match what is getting passed from Autocomplete's renderOption
  const itemData = children as ReactVirtualListItemOption[];
  const itemCount = itemData.length;
  const listHeight =
    itemCount < 8 ? getListHeight(itemCount) : getListHeight(8);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
  });

  const virtualizeItems = virtualizer.getVirtualItems();

  return (
    <Box ref={ref}>
      <Box
        ref={parentRef}
        {...listProps}
        sx={{
          height: listHeight,
          width: '100%',
          overflowY: 'auto',
          contain: 'strict',
        }}
      >
        <Box
          sx={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            component="ul"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualizeItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualizeItems.map((virtualItem) => {
              const item = itemData[virtualItem.index];
              const itemKey = item[0].key;

              return (
                <ReactVirtualListItem
                  key={itemKey}
                  listItemProps={item[0]}
                  option={item[1]}
                  dataIndex={virtualItem.index}
                  ref={virtualizer.measureElement}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
